import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { whiteLabelService } from '../services/whiteLabelService';
import { UserCircle, Mail, Lock, User, ArrowLeft } from 'lucide-react';

export default function Auth() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [branding, setBranding] = useState(null);
  
  const { signIn, signUp, resetPassword } = useAuth();
  const navigate = useNavigate();

  // Fetch branding settings on component mount
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const settings = await whiteLabelService?.getSettings();
        setBranding(settings);
      } catch (err) {
        console.error('Failed to load branding:', err);
      }
    };

    fetchBranding();
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      if (isForgotPassword) {
        // Handle password reset
        await resetPassword(email);
        setSuccessMessage('Password reset email sent! Please check your inbox and spam/junk folder for instructions to reset your password.');
        setEmail('');
        
        // Switch back to sign in after a delay
        setTimeout(() => {
          setIsForgotPassword(false);
          setSuccessMessage('');
        }, 5000);
      } else if (isSignUp) {
        // Validate required fields
        if (!fullName?.trim()) {
          throw new Error('Please enter your full name');
        }

        // Create account with basic info only
        const { data: newUser, error: signUpError } = await signUp(email, password, fullName);
        
        if (signUpError) {
          throw new Error(`Signup failed: ${signUpError.message}`);
        }

        setSuccessMessage('Account created successfully! Please check your email (including spam/junk folder) to verify your account, then sign in to complete your profile setup.');
        
        // Reset form and switch to sign in
        setEmail('');
        setPassword('');
        setFullName('');
        setIsSignUp(false);
      } else {
        // Sign in
        await signIn(email, password);
        setSuccessMessage('Sign in successful! Redirecting...');
        
        // Navigation will be handled by the onboarding check in App.jsx
        setTimeout(() => {
          navigate('/');
        }, 1000);
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError(err?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSignIn = () => {
    setIsForgotPassword(false);
    setIsSignUp(false);
    setError('');
    setSuccessMessage('');
    setEmail('');
    setPassword('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-900 via-blue-900 to-cyan-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* White Label Branding Header */}
        {branding ? (
          <div className="text-center mb-8">
            {branding?.logoUrl ? (
              <div className="flex justify-center mb-4">
                <img 
                  src={branding?.logoUrl} 
                  alt={branding?.brandName || 'Company Logo'} 
                  className="h-16 object-contain"
                />
              </div>
            ) : (
              <div className="mb-4">
                <h1 className="text-3xl font-bold text-white">{branding?.brandName || 'FinanceFlow'}</h1>
                {branding?.slogan && (
                  <p className="text-teal-200 mt-2">{branding?.slogan}</p>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Default Header */
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-4">
              <UserCircle className="w-8 h-8 text-teal-600" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">FinanceFlow</h1>
            <p className="text-teal-200">Professional Financial Management</p>
          </div>
        )}

        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">
            {isForgotPassword ? 'Reset Your Password' : isSignUp ? 'Create Your Account' : 'Welcome Back'}
          </h2>
          <p className="text-teal-100">
            {isForgotPassword 
              ? 'Enter your email to receive password reset instructions' 
              : isSignUp 
              ? 'Sign up to get started - complete your profile after verification' 
              : 'Sign in to access your dashboard'}
          </p>
        </div>

        {/* Login/Signup/Reset Card */}
        <div className="bg-white rounded-lg shadow-xl p-8">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Full Name - Only for signup */}
            {isSignUp && !isForgotPassword && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e?.target?.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="John Doe"
                    required
                  />
                </div>
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            {/* Password - Not shown in forgot password mode */}
            {!isForgotPassword && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e?.target?.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="••••••••"
                    minLength={6}
                  />
                </div>
              </div>
            )}

            {/* Forgot Password Link - Only shown on sign in */}
            {!isSignUp && !isForgotPassword && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setIsForgotPassword(true);
                    setError('');
                    setSuccessMessage('');
                  }}
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                >
                  Forgot Password?
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  {isForgotPassword ? 'Sending Reset Link...' : isSignUp ? 'Creating Account...' : 'Signing In...'}
                </>
              ) : (
                <>
                  <UserCircle className="w-5 h-5" />
                  {isForgotPassword ? 'Send Reset Link' : isSignUp ? 'Create Account' : 'Sign In'}
                </>
              )}
            </button>
          </form>

          {/* Back to Sign In Button - Shown in forgot password and signup modes */}
          {(isForgotPassword || isSignUp) && (
            <div className="mt-6">
              <button
                type="button"
                onClick={handleBackToSignIn}
                className="w-full flex items-center justify-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Sign In
              </button>
            </div>
          )}

          {/* Toggle Sign Up/Sign In - Only shown when not in forgot password mode */}
          {!isForgotPassword && (
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setError('');
                  setSuccessMessage('');
                }}
                className="text-teal-600 hover:text-teal-700 font-medium text-sm"
              >
                {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
              </button>
            </div>
          )}

          {isSignUp && !isForgotPassword && (
            <p className="text-xs text-gray-500 text-center mt-4">
              By signing up, you will receive an email confirmation link. After verification, you'll complete your business profile during your first login.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}